import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { authService } from '../../services/authService';
import StateSelector from '../Common/StateSelector';
import {
  UserIcon,
  PhotoIcon,
  PencilIcon,
  CheckIcon,
  XMarkIcon,
  EyeIcon,
  EyeSlashIcon,
  KeyIcon,
  PhoneIcon,
  MapPinIcon,
  BuildingOfficeIcon,
  ClockIcon,
  IdentificationIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';

const ProfilePage = () => {
  const { user, updateUser } = useAuth();
  const [profile, setProfile] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    homeAddress: '',
    state: '',
    username: '',
    profileImage: '',
  });
  const [agentDetails, setAgentDetails] = useState({
    agentImage: '',
    agent_name: '',
    phone_number: '',
    localGovernmentAddress: '',
    streetAddress: '',
    verificationMethod: '',
    verificationNumber: '',
    openHours: {
      monday: { open: '', close: '' },
      tuesday: { open: '', close: '' },
      wednesday: { open: '', close: '' },
      thursday: { open: '', close: '' },
      friday: { open: '', close: '' },
      saturday: { open: '', close: '' },
      sunday: { open: '', close: '' },
    },
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [editing, setEditing] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [agentImagePreview, setAgentImagePreview] = useState(null);
  const [spotlightLocations, setSpotlightLocations] = useState([]);
  const [newSpotlightLocation, setNewSpotlightLocation] = useState('');
  const [newSpotlightTime, setNewSpotlightTime] = useState('');

  useEffect(() => {
    loadUserProfile();
  }, []);

  const loadUserProfile = async () => {
    try {
      setLoading(true);
      const response = await authService.getMe();
      const userData = response.data || response;
      
      setProfile({
        firstName: userData.firstName || '',
        lastName: userData.lastName || '',
        email: userData.email || '',
        phoneNumber: userData.phoneNumber || '',
        homeAddress: userData.homeAddress || '',
        state: userData.state || '',
        username: userData.username || '',
        profileImage: userData.profileImage || '',
      });

      if (userData.isAgent && userData.agentDetails) {
        setAgentDetails({
          agentImage: userData.agentDetails.agentImage || '',
          agent_name: userData.agentDetails.agent_name || '',
          phone_number: userData.agentDetails.phone_number || '',
          localGovernmentAddress: userData.agentDetails.localGovernmentAddress || '',
          streetAddress: userData.agentDetails.streetAddress || '',
          verificationMethod: userData.agentDetails.verificationMethod || '',
          verificationNumber: userData.agentDetails.verificationNumber || '',
          openHours: userData.agentDetails.openHours || {
            monday: { open: '', close: '' },
            tuesday: { open: '', close: '' },
            wednesday: { open: '', close: '' },
            thursday: { open: '', close: '' },
            friday: { open: '', close: '' },
            saturday: { open: '', close: '' },
            sunday: { open: '', close: '' },
          },
        });
      }
      setSpotlightLocations(Array.isArray(userData.spotlightLocations) ? userData.spotlightLocations : []);
    } catch (error) {
      console.error('Error loading profile:', error);
      setMessage('Error loading profile');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = (e, type) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        if (type === 'profile') {
          setImagePreview(e.target.result);
          setProfile({ ...profile, profileImage: e.target.result });
        } else if (type === 'agent') {
          setAgentImagePreview(e.target.result);
          setAgentDetails({ ...agentDetails, agentImage: e.target.result });
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSaveProfile = async () => {
    try {
      setSaving(true);
      setMessage('');

      const response = await authService.updateProfile(profile);
      if (user.isAgent) {
        // Only send editable agent fields (exclude verificationMethod and verificationNumber)
        const editableAgentDetails = {
          agent_name: agentDetails.agent_name,
          localGovernmentAddress: agentDetails.localGovernmentAddress,
          streetAddress: agentDetails.streetAddress,
        };
        await authService.updateAgentDetails(editableAgentDetails);
        await authService.updateSpotlightLocations(spotlightLocations);
      }

      // Fetch updated user data to ensure we have the latest information
      const updatedUserData = await authService.getMe();
      updateUser({
        ...user,
        ...profile,
        agentDetails: updatedUserData.agentDetails || user.agentDetails,
        spotlightLocations: updatedUserData.spotlightLocations || spotlightLocations,
      });
      setMessage('Profile updated successfully!');
      setEditing(false);
    } catch (error) {
      console.error('Error updating profile:', error);
      setMessage(error.response?.data?.message || 'Error updating profile');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    try {
      setSaving(true);
      setMessage('');

      if (passwordData.newPassword !== passwordData.confirmPassword) {
        setMessage('New passwords do not match');
        return;
      }

      await authService.changePassword(passwordData);
      setMessage('Password changed successfully!');
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setShowPasswordForm(false);
    } catch (error) {
      console.error('Error changing password:', error);
      setMessage(error.response?.data?.message || 'Error changing password');
    } finally {
      setSaving(false);
    }
  };

  const formatWorkingHours = (day) => {
    const hours = agentDetails.openHours[day];
    if (!hours || !hours.open || !hours.close) return 'Closed';
    return `${hours.open} - ${hours.close}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
              {profile.profileImage || imagePreview ? (
                <img
                  src={imagePreview || profile.profileImage}
                  alt="Profile"
                  className="w-16 h-16 rounded-full object-cover"
                />
              ) : (
                <UserIcon className="h-8 w-8 text-green-600" />
              )}
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {profile.firstName} {profile.lastName}
              </h1>
              <p className="text-gray-600">{profile.email}</p>
              {user.isAgent && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  Verified Agent
                </span>
              )}
            </div>
          </div>
          <div className="flex space-x-2">
            {!editing ? (
              <button
                onClick={() => setEditing(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700"
              >
                <PencilIcon className="h-4 w-4 mr-2" />
                Edit Profile
              </button>
            ) : (
              <>
                <button
                  onClick={() => {
                    setEditing(false);
                    loadUserProfile();
                  }}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  <XMarkIcon className="h-4 w-4 mr-2" />
                  Cancel
                </button>
                <button
                  onClick={handleSaveProfile}
                  disabled={saving}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
                >
                  <CheckIcon className="h-4 w-4 mr-2" />
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Message */}
      {message && (
        <div
          className={`p-4 rounded-md ${
            message.includes('successfully')
              ? 'bg-green-100 text-green-700 border border-green-200'
              : 'bg-red-100 text-red-700 border border-red-200'
          }`}
        >
          {message}
        </div>
      )}

      {/* Profile Information */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Personal Information</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              First Name
            </label>
            <input
              type="text"
              value={profile.firstName}
              onChange={(e) => setProfile({ ...profile, firstName: e.target.value })}
              disabled={!editing}
              placeholder="Enter your first name"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 disabled:bg-gray-100"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Last Name
            </label>
            <input
              type="text"
              value={profile.lastName}
              onChange={(e) => setProfile({ ...profile, lastName: e.target.value })}
              disabled={!editing}
              placeholder="Enter your last name"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 disabled:bg-gray-100"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email
            </label>
            <input
              type="email"
              value={profile.email}
              onChange={(e) => setProfile({ ...profile, email: e.target.value })}
              disabled={!editing}
              placeholder="Enter your email address"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 disabled:bg-gray-100"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Phone Number
            </label>
            <input
              type="tel"
              value={profile.phoneNumber}
              onChange={(e) => setProfile({ ...profile, phoneNumber: e.target.value })}
              disabled={true}
              placeholder="Enter your phone number"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 disabled:bg-gray-100 cursor-not-allowed"
            />
            <p className="mt-1 text-xs text-gray-500">
              Phone number cannot be changed.
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Username
            </label>
            <input
              type="text"
              value={profile.username}
              onChange={(e) => setProfile({ ...profile, username: e.target.value })}
              disabled={!editing}
              placeholder="Enter your username"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 disabled:bg-gray-100"
            />
          </div>
          <div>
            <StateSelector
              value={profile.state}
              onChange={(state) => setProfile({ ...profile, state })}
              placeholder="Select your state"
              disabled={!editing}
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Home Address
            </label>
            <textarea
              value={profile.homeAddress}
              onChange={(e) => setProfile({ ...profile, homeAddress: e.target.value })}
              disabled={!editing}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 disabled:bg-gray-100"
            />
          </div>
        </div>

        {/* Profile Image Upload */}
        {editing && (
          <div className="mt-6 pt-6 border-t border-gray-200">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Profile Image
            </label>
            <div className="flex items-center space-x-4">
              <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center">
                {profile.profileImage || imagePreview ? (
                  <img
                    src={imagePreview || profile.profileImage}
                    alt="Profile Preview"
                    className="w-20 h-20 rounded-full object-cover"
                  />
                ) : (
                  <PhotoIcon className="h-8 w-8 text-gray-400" />
                )}
              </div>
              <div>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleImageUpload(e, 'profile')}
                  className="hidden"
                  id="profile-image-upload"
                />
                <label
                  htmlFor="profile-image-upload"
                  className="cursor-pointer inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  <PhotoIcon className="h-4 w-4 mr-2" />
                  Upload Image
                </label>
                <p className="text-xs text-gray-500 mt-1">
                  JPG, PNG or GIF (max 5MB)
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Change Password */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Security</h2>
          {!showPasswordForm ? (
            <button
              onClick={() => setShowPasswordForm(true)}
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              <KeyIcon className="h-4 w-4 mr-2" />
              Change Password
            </button>
          ) : (
            <button
              onClick={() => setShowPasswordForm(false)}
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              Cancel
            </button>
          )}
        </div>

        {showPasswordForm && (
          <div className="space-y-4 pt-4 border-t border-gray-200">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Current Password
              </label>
              <div className="relative">
                <input
                  type={showCurrentPassword ? 'text' : 'password'}
                  value={passwordData.currentPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                  placeholder="Enter your current password"
                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  {showCurrentPassword ? (
                    <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                  ) : (
                    <EyeIcon className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                New Password
              </label>
              <div className="relative">
                <input
                  type={showNewPassword ? 'text' : 'password'}
                  value={passwordData.newPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                  placeholder="Enter your new password"
                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  {showNewPassword ? (
                    <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                  ) : (
                    <EyeIcon className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Confirm New Password
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                  placeholder="Confirm your new password"
                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  {showConfirmPassword ? (
                    <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                  ) : (
                    <EyeIcon className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              </div>
            </div>
            <button
              onClick={handleChangePassword}
              disabled={saving}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
            >
              <KeyIcon className="h-4 w-4 mr-2" />
              {saving ? 'Changing...' : 'Change Password'}
            </button>
          </div>
        )}
      </div>

      {/* Agent Details */}
      {user.isAgent && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Agent Details</h2>
          
          {/* Agent Image */}
          {editing && (
            <div className="mb-6 pb-6 border-b border-gray-200">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Agent Image
              </label>
              <div className="flex items-center space-x-4">
                <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center">
                  {agentDetails.agentImage || agentImagePreview ? (
                    <img
                      src={agentImagePreview || agentDetails.agentImage}
                      alt="Agent Preview"
                      className="w-20 h-20 rounded-full object-cover"
                    />
                  ) : (
                    <PhotoIcon className="h-8 w-8 text-gray-400" />
                  )}
                </div>
                <div>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageUpload(e, 'agent')}
                    className="hidden"
                    id="agent-image-upload"
                  />
                  <label
                    htmlFor="agent-image-upload"
                    className="cursor-pointer inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  >
                    <PhotoIcon className="h-4 w-4 mr-2" />
                    Upload Agent Image
                  </label>
                  <p className="text-xs text-gray-500 mt-1">
                    JPG, PNG or GIF (max 5MB)
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Agent Name
              </label>
              <input
                type="text"
                value={agentDetails.agent_name}
                onChange={(e) => setAgentDetails({ ...agentDetails, agent_name: e.target.value })}
                disabled={!editing}
                placeholder="Enter your agent name"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 disabled:bg-gray-100"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Agent Phone
              </label>
              <input
                type="tel"
                value={agentDetails.phone_number}
                onChange={(e) => setAgentDetails({ ...agentDetails, phone_number: e.target.value })}
                disabled={!editing}
                placeholder="Enter your agent phone number"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 disabled:bg-gray-100"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Verification Method
              </label>
              <select
                value={agentDetails.verificationMethod}
                onChange={(e) => setAgentDetails({ ...agentDetails, verificationMethod: e.target.value })}
                disabled={true}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 disabled:bg-gray-100 cursor-not-allowed"
              >
                <option value="">Select Method</option>
                <option value="NIN">NIN</option>
                <option value="Voters card">Voters Card</option>
              </select>
              <p className="mt-1 text-xs text-gray-500">
                Verification method cannot be changed.
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Verification Number
              </label>
              <input
                type="text"
                value={agentDetails.verificationNumber}
                onChange={(e) => setAgentDetails({ ...agentDetails, verificationNumber: e.target.value })}
                disabled={true}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 disabled:bg-gray-100 cursor-not-allowed"
              />
              <p className="mt-1 text-xs text-gray-500">
                Verification number cannot be changed.
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Local Government Area
              </label>
              <input
                type="text"
                value={agentDetails.localGovernmentAddress}
                onChange={(e) => setAgentDetails({ ...agentDetails, localGovernmentAddress: e.target.value })}
                disabled={!editing}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 disabled:bg-gray-100"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Street Address
              </label>
              <input
                type="text"
                value={agentDetails.streetAddress}
                onChange={(e) => setAgentDetails({ ...agentDetails, streetAddress: e.target.value })}
                disabled={!editing}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 disabled:bg-gray-100"
              />
            </div>
          </div>

          {/* Spotlighted Locations */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <h3 className="text-md font-medium text-gray-900 mb-4">Spotlighted Locations</h3>
            <p className="text-sm text-gray-600 mb-4">
              List locations and times when you will be available for pickup. These will be shown to sellers on the sell page.
            </p>

            {/* Always show the inputs so agents can add locations easily */}
            <div className="flex flex-wrap gap-3 mb-4">
              <input
                type="text"
                value={newSpotlightLocation}
                onChange={(e) => setNewSpotlightLocation(e.target.value)}
                placeholder="Location (e.g. Main Market, Ikeja)"
                className="flex-1 min-w-[140px] px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
              />
              <input
                type="text"
                value={newSpotlightTime}
                onChange={(e) => setNewSpotlightTime(e.target.value)}
                placeholder="Time (e.g. 9am - 2pm)"
                className="flex-1 min-w-[140px] px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
              />
              <button
                type="button"
                onClick={async () => {
                  const location = newSpotlightLocation.trim();
                  const time = newSpotlightTime.trim();
                  if (!location && !time) return;

                  const updated = [
                    ...spotlightLocations,
                    { location, time },
                  ];

                  setSpotlightLocations(updated);
                  setNewSpotlightLocation('');
                  setNewSpotlightTime('');

                  try {
                    await authService.updateSpotlightLocations(updated);
                    setMessage('Spotlight locations updated successfully!');
                  } catch (error) {
                    console.error('Error updating spotlight locations:', error);
                    setMessage(
                      error.response?.data?.message || 'Error updating spotlight locations'
                    );
                  }
                }}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700"
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                Add
              </button>
            </div>

            <ul className="space-y-2">
              {spotlightLocations.length === 0 ? (
                <li className="text-sm text-gray-500">No spotlight locations added yet.</li>
              ) : (
                spotlightLocations.map((item, index) => (
                  <li
                    key={index}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200"
                  >
                    <div className="flex-1">
                      <span className="font-medium text-gray-900">{item.location || '—'}</span>
                      {item.time && <span className="text-gray-600 ml-2">• {item.time}</span>}
                    </div>
                    <button
                      type="button"
                      onClick={async () => {
                        const updated = spotlightLocations.filter((_, i) => i !== index);
                        setSpotlightLocations(updated);

                        try {
                          await authService.updateSpotlightLocations(updated);
                          setMessage('Spotlight locations updated successfully!');
                        } catch (error) {
                          console.error('Error updating spotlight locations:', error);
                          setMessage(
                            error.response?.data?.message || 'Error updating spotlight locations'
                          );
                        }
                      }}
                      className="p-2 text-red-600 hover:bg-red-50 rounded"
                      title="Remove"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </li>
                ))
              )}
            </ul>
          </div>

          {/* Working Hours */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <h3 className="text-md font-medium text-gray-900 mb-4">Working Hours</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Object.keys(agentDetails.openHours).map((day) => (
                <div key={day} className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700 capitalize">
                    {day}
                  </label>
                  <div className="flex space-x-2">
                    <input
                      type="time"
                      value={agentDetails.openHours[day].open}
                      onChange={(e) => setAgentDetails({
                        ...agentDetails,
                        openHours: {
                          ...agentDetails.openHours,
                          [day]: { ...agentDetails.openHours[day], open: e.target.value }
                        }
                      })}
                      disabled={!editing}
                      className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-green-500 disabled:bg-gray-100"
                    />
                    <input
                      type="time"
                      value={agentDetails.openHours[day].close}
                      onChange={(e) => setAgentDetails({
                        ...agentDetails,
                        openHours: {
                          ...agentDetails.openHours,
                          [day]: { ...agentDetails.openHours[day], close: e.target.value }
                        }
                      })}
                      disabled={!editing}
                      className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-green-500 disabled:bg-gray-100"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfilePage;
